import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import {
  CenteredShell,
  FieldLabel,
  OnboardingExit,
  PageEyebrow,
} from '../components/auth-shells.js';
import { Mag } from '../components/mag.js';
import { magToast } from '../components/toaster.js';
import { scrapeUrlIntoBrief } from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import { brandsQuery, meQuery } from '../lib/queries.js';
import { isValidSiteUrl } from '../lib/site-url.js';

// §12.2 — the standalone URL step. The happy path now folds this into the
// /brands create form (paste the site there → brand created + read kicked
// off → straight to the live scrape page). This route stays as the
// fallback/resume surface: re-scrape from the scrape page's "use a
// different url", a scrape that couldn't be started, or a direct revisit.
// Founder pastes their marketing site URL; the api scrapes + LLM-extracts
// a draft brief; we send them to the live scrape page. Skip → /brief empty.

export const Route = createFileRoute('/brand/$slug/onboarding/url')({
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const brand = brands.find((b) => b.slug === params.slug);
    if (!brand) throw redirect({ to: '/brands' });
    return { me, brand, brands };
  },
  loader: ({ context }) =>
    context as {
      me: unknown;
      brand: { id: string; name: string; slug: string; archivedAt: string | null };
      brands: { id: string; name: string; slug: string; archivedAt: string | null }[];
    },
  component: OnboardingUrlPage,
});

function OnboardingUrlPage() {
  const { t } = useLingui();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { brand, brands } = Route.useLoaderData();
  useBrandTitle(brand.slug, ['onboarding']);
  const [url, setUrl] = useState('');
  const [working, setWorking] = useState(false);
  const liveBrands = brands.filter((b) => b.archivedAt === null);
  const showBrandLabel = liveBrands.length > 1;
  const urlIsValid = isValidSiteUrl(url);

  // Auto-focus the input AFTER the stagger reveal completes (~500ms)
  // so the focus animation doesn't compete with the entry animation.
  // blinking cursor signals "this form is live, type here", but only
  // once the form has actually finished animating in.
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const timer = window.setTimeout(() => inputRef.current?.focus(), 500);
    return () => window.clearTimeout(timer);
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setWorking(true);
    try {
      const result = await scrapeUrlIntoBrief(url, brand.id);
      // §12.2L — endpoint returns instantly with runId; navigate to
      // the live page so the founder watches mag read the site in
      // real time. The brain row gets populated by the orchestrator
      // running in the background; invalidate the brain query so the
      // live page (and any downstream surface) pulls fresh data as
      // sections land.
      //
      // [12.2L-54] Also invalidate brands — the api endpoint stamps
      // brand.marketingUrl synchronously now, so the cache needs to
      // pick that up before /onboarding/scrape's beforeLoad reads
      // the brand row. without this, brand.marketingUrl is still
      // null in the scrape page and the "start over" button errors
      // with "i don't have a marketing url for this brand".
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['brand-brain'] }),
        queryClient.invalidateQueries({ queryKey: ['brands'] }),
      ]);
      navigate({
        to: '/brand/$slug/onboarding/scrape',
        params: { slug: brand.slug },
        search: { runId: result.runId },
      });
    } catch (err) {
      magToast.error(
        "mag couldn't read that site.",
        founderErrorCopy(err, 'try again, or skip for now.'),
      );
      setWorking(false);
    }
  };

  return (
    <CenteredShell>
      <OnboardingExit brandSlug={brand.slug} />
      {/* Mag's first-impression pose — alert eyes ('default') wrapped
          in a softly-pulsing aura + breathing scale so the founder's
          first frame after signup reads as "she's awake, ready,
          listening" instead of the prior 'smug' (closed-eye) which
          read as napping / broken. Same aura/breathe vocabulary used
          on the live scrape page so the visual language carries
          through the onboarding arc. */}
      <div className="relative">
        <div
          aria-hidden="true"
          className="mag-aura pointer-events-none absolute inset-[-18px] rounded-full"
        />
        <div className="mag-breathe relative">
          <Mag size={86} mood={working ? 'casting' : 'default'} />
        </div>
      </div>
      <div className="mag-stagger-in" style={{ animationDelay: '80ms' }}>
        <PageEyebrow>
          <Trans>
            onboarding · step 1 of 6 · your site
            {showBrandLabel ? ` · ${brand.name.toLowerCase()}` : ''}
          </Trans>
        </PageEyebrow>
      </div>
      <h1
        className="mag-stagger-in font-display text-[40px] sm:text-[60px] font-black leading-[1] tracking-[-0.045em]"
        style={{ animationDelay: '160ms' }}
      >
        <Trans>paste your site.</Trans>
      </h1>
      <p
        className="mag-stagger-in font-serif text-xl leading-tight text-[var(--ink)]/80 whitespace-nowrap"
        style={{ animationDelay: '240ms' }}
      >
        <Trans>“i'll read it and write the brief for you. you'll review what i got.”</Trans>
      </p>

      <form
        onSubmit={handleSubmit}
        className="mag-stagger-in mt-2 flex w-full max-w-[460px] flex-col items-stretch gap-3"
        style={{ animationDelay: '320ms' }}
      >
        <label className="field">
          <FieldLabel>
            <Trans>your marketing site</Trans>
          </FieldLabel>
          {/* Static https:// prefix as a left adornment — the founder
              types just the host so they don't carry the protocol
              themselves. If they paste a full URL anyway, the onChange
              handler strips the leading scheme so the field never
              double-renders "https://https://…". The validator already
              prepends https:// before parsing, so the submitted value
              works either way. */}
          <div className="relative">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-[14px] top-1/2 -translate-y-1/2 select-none font-mono text-[14px] text-[var(--ink)]/45"
            >
              {'https://'}
            </span>
            <input
              ref={inputRef}
              type="text"
              inputMode="url"
              autoComplete="url"
              required
              placeholder={t`yourbrand.com`}
              value={url}
              onChange={(e) => setUrl(e.target.value.replace(/^https?:\/\//i, ''))}
              disabled={working}
              style={{ paddingLeft: '88px' }}
            />
          </div>
        </label>
        <div className="mt-1 flex items-center justify-end gap-3">
          {/* Three states, three treatments:
                - invalid url  → ghost outline (`btn btn-ghost`) so the
                  empty form reads as "next step waiting on you", not
                  "broken button".
                - valid url    → solid primary (`btn`) — the form is
                  ready and mag is ready.
                - working      → primary + opacity-60 + label change
                  ("reading…") so the in-flight state reads as work,
                  not disabled. */}
          <button
            type="submit"
            disabled={working || !urlIsValid}
            className={urlIsValid || working ? 'btn disabled:opacity-60' : 'btn btn-ghost'}
          >
            {working ? <Trans>reading…</Trans> : <Trans>let mag read it</Trans>}
            <span className="btn-arrow">→</span>
          </button>
        </div>
      </form>
      {working ? (
        <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--ink)]/55">
          <Trans>
            this can take 10–20 seconds. mag is reading your hero, your testimonials, your fine
            print.
          </Trans>
        </p>
      ) : (
        // Idle hint — fills the empty space below the form with a
        // mag-voice line about what she'll do once the founder submits.
        // softens the cold cream + makes the page feel less static.
        <p
          className="mag-stagger-in mt-3 mag-voice text-[13.5px] leading-[1.5] text-[var(--ink)]/55"
          style={{ animationDelay: '480ms' }}
        >
          <Trans>
            while i read: your voice, your icp, your colors, your products. you'll see it all on the
            next screen.
          </Trans>
        </p>
      )}
    </CenteredShell>
  );
}
